// Figma frame 14 · Documents (33:1588) — US-120.
//
// PRD 17 §2: "Ingest 40 documents and nothing lists them. Navigation requires
// knowing the ID." This screen and `GET /docs` are jointly that fix, so the
// one thing it may never do is make a document unreachable: every row links
// to its review screen (US-121).

import { useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import "./content.css";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  PageHeader,
  Select,
  SkeletonStack,
  Skeleton,
  Table,
  type TableColumn,
} from "../../ui";
import { useDocList, type DocStatus, type DocSummary } from "./api";
import { hasUnavailable, presentStatus } from "./docStatus";
import { relativeTime } from "./relativeTime";
import { FilterChip, Pager } from "./controls";

// MEASURED, not chosen. `Screen/Template`'s body slot is 968px tall, a table
// row is 52px, and the rest of the screen (page header, filter row, card
// chrome, count line, pager) costs ~380px — so 10 rows is the largest page
// that fits one screen without the body scrolling. The server's own default
// is 20, which overflows by ~400px; Figma draws 6 with "Showing 1–6 of 9",
// which is legible but turns 60 documents into ten pages.
const PAGE_SIZE = 10;

const STATUS_FILTERS: { value: DocStatus; label: string }[] = [
  { value: "published", label: "Published" },
  { value: "extracted", label: "In review" },
  { value: "not_extracted", label: "Draft" },
];

// The server sorts on `updated_at` and `term_count` only — a title sort has
// no backing parameter, so the drawn sort caret on the DOCUMENT column is not
// offered here rather than faked client-side over one page of 20.
const SORTS = [
  { value: "updated_at:desc", label: "Recently updated" },
  { value: "updated_at:asc", label: "Least recently updated" },
  { value: "term_count:desc", label: "Most terms" },
  { value: "term_count:asc", label: "Fewest terms" },
];

export function DocumentsScreen({ orgSlug }: { orgSlug: string }) {
  const navigate = useNavigate();
  const [offset, setOffset] = useState(0);
  const [status, setStatus] = useState<DocStatus | null>(null);
  const [sortKey, setSortKey] = useState("updated_at:desc");
  const [sort, order] = sortKey.split(":") as ["updated_at" | "term_count", "asc" | "desc"];

  const query = useDocList({ limit: PAGE_SIZE, offset, sort, order, status });

  const toggleStatus = (value: DocStatus) => {
    setStatus((current) => (current === value ? null : value));
    setOffset(0);
  };

  return (
    <>
      <PageHeader
        eyebrow="Workspace"
        title="Documents"
        description={`Every document in the ${orgSlug} corpus and how much of its jargon is explained.`}
        actions={
          <>
            <Button variant="ghost" onClick={() => navigate({ to: "/sources" })}>
              Add a source
            </Button>
            <Button variant="primary" onClick={() => navigate({ to: "/sources/add" })}>
              Ingest a doc
            </Button>
          </>
        }
      />

      <div className="maisight-content-filters">
        <Select
          aria-label="Sort documents"
          options={SORTS}
          value={sortKey}
          onChange={(event) => {
            setSortKey(event.target.value);
            setOffset(0);
          }}
        />
        {STATUS_FILTERS.map((filter) => (
          <FilterChip
            key={filter.value}
            label={filter.label}
            pressed={status === filter.value}
            onToggle={() => toggleStatus(filter.value)}
          />
        ))}
      </div>

      <Body
        orgSlug={orgSlug}
        offset={offset}
        onOffsetChange={setOffset}
        query={query}
      />
    </>
  );
}

type BodyProps = {
  orgSlug: string;
  offset: number;
  onOffsetChange: (offset: number) => void;
  query: ReturnType<typeof useDocList>;
};

function Body({ orgSlug, offset, onOffsetChange, query }: BodyProps) {
  if (query.isPending) {
    return (
      <SkeletonStack label="Loading documents">
        <Skeleton width={280} height={28} />
        <Skeleton width={620} />
        <Skeleton width={452} />
      </SkeletonStack>
    );
  }

  // 401 is "you are signed out", which is a different screen from "you have
  // no documents". Wave 1 corrected exactly this conflation on the server;
  // rendering both as an empty library would put it straight back.
  if (query.error?.status === 401) {
    return (
      <ErrorState
        headline="You're signed out"
        detail="Sign in again to see this workspace's documents."
        action={<Link to="/signin">Sign in</Link>}
      />
    );
  }
  if (query.error || !query.data) {
    return (
      <ErrorState
        headline="Couldn't load your documents"
        detail="The document list did not respond. Try again in a moment."
      />
    );
  }

  const { items, total } = query.data;

  // 200 with total 0 — authenticated, genuinely nothing ingested yet.
  if (total === 0) {
    return (
      <EmptyState
        icon="file-text"
        headline="No documents yet"
        body="Ingest a doc or connect a source, and every term mAiSight finds in it shows up here."
        action={<Link to="/sources/add">Add a source</Link>}
      />
    );
  }

  return (
    <Card tone="flat" header="All documents">
      {hasUnavailable(items.map((item) => item.status)) ? (
        <p className="maisight-content-notice type-bodyS" role="status">
          Extraction status is unavailable for some documents: no language-model
          key is configured on this deployment. The documents are here and
          intact — only their extraction state is unknown.
        </p>
      ) : null}
      <Table<DocSummary>
        label="Documents"
        columns={columns(orgSlug)}
        rows={items}
        rowKey={(row) => row.doc_id}
        maxRows={PAGE_SIZE}
        total={total}
        unit="documents"
      />
      <Pager
        label="Documents pages"
        offset={offset}
        limit={PAGE_SIZE}
        total={total}
        onOffsetChange={onOffsetChange}
      />
    </Card>
  );
}

function columns(orgSlug: string): TableColumn<DocSummary>[] {
  return [
    {
      key: "document",
      header: "Document",
      cell: (row) => (
        // `/docs/$docId/review` has no org in its path, so the shell's org
        // rides in the search param the review route now validates.
        <Link
          className="maisight-content-doc-link"
          to="/docs/$docId/review"
          params={{ docId: row.doc_id }}
          search={{ org: orgSlug }}
        >
          {row.title ?? row.doc_id}
        </Link>
      ),
    },
    { key: "terms", header: "Terms", align: "right", cell: (row) => row.term_count },
    {
      key: "status",
      header: "Status",
      cell: (row) => {
        const shown = presentStatus(row.status);
        return (
          <span title={shown.detail}>
            <Badge tone={shown.tone} lamp>
              {shown.label}
            </Badge>
          </span>
        );
      },
    },
    { key: "updated", header: "Updated", cell: (row) => relativeTime(row.updated_at) },
  ];
}
